<template>
  <div
    class="space-y-4 divide-y divide-gray-200 rounded-5 border border-gray-200"
    :class="{ 'bg-gray-50': tabBlock.type === BlockType.TILES }"
    data-cy="block-container"
  >
    <div
      class="flex items-center justify-between bg-white px-4 py-2"
      @click="toggleCollapsed"
    >
      <div class="flex items-center space-x-2">
        <button
          class="drag-handle flex cursor-grab items-center justify-center transition-opacity active:cursor-grabbing"
          data-cy="block-drag-handle"
          @click.stop
        >
          <icon-base
            class="h-3 w-2 text-gray-400"
            height="12"
            width="7"
            :icon="IconDragHandle"
            viewBox="0 0 12 20"
          />
        </button>
        <icon-base
          v-if="blockTypeData.icon"
          :icon="blockTypeData.icon"
          class="h-5 w-5 text-gray-500"
        />
        <span class="text-sm font-medium text-gray-700">
          {{ blockTypeData.label }}
        </span>
      </div>

      <div class="flex items-center space-x-2">
        <button
          v-if="!disabled"
          class="p-1 text-gray-500 hover:text-gray-600"
          data-cy="delete-block-button"
          @click.stop="showDeleteModal = true"
        >
          <icon-base :icon="IconTrashCanOutline" class="h-4 w-4" />
        </button>
        <button
          class="p-1 text-gray-500 hover:text-gray-700"
          data-cy="toggle-block-collapse"
          @click.stop="toggleCollapsed"
        >
          <dropdown-chevron
            :is-open="!isCollapsed"
            :is-default-position="false"
          />
        </button>
      </div>
    </div>

    <div v-if="!isCollapsed" class="p-4">
      <div
        v-if="tabBlock.type !== BlockType.TILES"
        class="mb-4 text-xs font-semibold uppercase"
      >
        {{ $t("BORROWER_PLATFORM.POSITION") }}
      </div>
      <block-position-controls
        v-if="tabBlock.type !== BlockType.TILES"
        :model-value="tabBlock.align"
        :disabled="disabled"
        class="mb-4"
        @update:model-value="handlePositionChange"
      />
      <slot></slot>
    </div>
    <confirm-modal
      v-if="showDeleteModal"
      :title="$t('BORROWER_PLATFORM.DELETE_BLOCK_TITLE')"
      :description="$t('BORROWER_PLATFORM.DELETE_BLOCK_MESSAGE')"
      :close="() => (showDeleteModal = false)"
      :confirm-button="$t('COMMON.DELETE')"
      confirm-button-type="error"
      data-cy="delete-block-confirm-modal"
      @confirm="handleBlockDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { BLOCK_TYPES } from "@/helpers/constants/borrowerPlatform";
import {
  type Block,
  BlockType,
  type BlockWithIcon,
} from "@/models/borrowerPlatformTemplates";
import { useBorrowerPlatformTemplateBuilderStore } from "@/stores/borrowerPlatformTemplates";

import BlockPositionControls from "./Controlls/BlockPositionControls.vue";
import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { BlockAlignment } from "@/models/borrowerPlatformTemplates";

const borrowerPlatformTemplateStore = useBorrowerPlatformTemplateBuilderStore();

interface Props {
  tabBlock: Block;
  disabled?: boolean;
}

const { tabBlock, disabled = false } = defineProps<Props>();

const { t } = useI18n();

const isCollapsed = ref(false);
const showDeleteModal = ref(false);

const { selectedTabId } = storeToRefs(borrowerPlatformTemplateStore);

const { deleteBlockFromBorrowerPlatformTab, updateBorrowerPlatformBlock } =
  borrowerPlatformTemplateStore;

const blockTypeData = computed(() => {
  const definition = getBlockTypeDefinition(tabBlock.type);
  return {
    icon: definition?.iconComponent,
    label: definition
      ? t(definition.labelKey)
      : t("BORROWER_PLATFORM.BLOCK_TYPE.UNKNOWN"),
  };
});

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value;
};

const getBlockTypeDefinition = (type: BlockType): BlockWithIcon | undefined => {
  return BLOCK_TYPES.find((blockType) => blockType.type === type);
};

const handlePositionChange = (position: BlockAlignment | undefined) => {
  if (!position || !selectedTabId.value || !tabBlock.id || disabled) {
    return;
  }

  updateBorrowerPlatformBlock({
    tabId: selectedTabId.value,
    blockId: tabBlock.id,
    updates: { ...tabBlock, align: position },
  });
};

const handleBlockDelete = () => {
  if (!selectedTabId.value || !tabBlock.id || disabled) {
    return;
  }

  deleteBlockFromBorrowerPlatformTab(selectedTabId.value, tabBlock.id);

  showDeleteModal.value = false;
};
</script>
